/**
 * PA Todo Page
 * My tasks, assigned tasks, team view. Expandable details with email source.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Circle, Clock, Loader2, Plus, User, Users, Bell, Mail, X, ChevronDown, ChevronUp, Sparkles, Tag, MessageSquare, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';
import apiClient from '@/services/api/ma-client';
import { SkeletonPulse2 } from '@/components/pwa/Skeleton';
import { FormattedChatMessage } from '@/components/chat/ChatRenderer';

type Tab = 'my' | 'assigned' | 'team';

export default function TodoPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('my');
  const [showCreate, setShowCreate] = useState(false);
  const [newTodo, setNewTodo] = useState({ title: '', description: '', priority: 'medium', dueDate: '', assignedTo: '' });
  const [showCompleted, setShowCompleted] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: stats } = useQuery({ queryKey: ['todo-stats'], queryFn: async () => { const r = await apiClient.get('/todo/stats'); return r.data.data; } });
  const { data: myTodos, isLoading: loadingMy } = useQuery({ queryKey: ['todo-my', showCompleted ? 'all' : 'pending'], queryFn: async () => { const r = await apiClient.get(`/todo/my?status=${showCompleted ? 'all' : 'pending'}`); return r.data.data || []; }, enabled: tab === 'my' });
  const { data: assignedTodos, isLoading: loadingAssigned } = useQuery({ queryKey: ['todo-assigned'], queryFn: async () => { const r = await apiClient.get('/todo/assigned'); return r.data.data || []; }, enabled: tab === 'assigned' });
  const { data: teamTodos, isLoading: loadingTeam } = useQuery({ queryKey: ['todo-team'], queryFn: async () => { const r = await apiClient.get('/todo/team'); return r.data.data || []; }, enabled: tab === 'team' });
  const { data: teamMembers } = useQuery({ queryKey: ['todo-users'], queryFn: async () => { const r = await apiClient.get('/todo/users'); return r.data.data || []; } });

  const invalidateAll = () => { ['todo-my', 'todo-stats', 'todo-assigned', 'todo-team'].forEach(k => queryClient.invalidateQueries({ queryKey: [k] })); };
  const createMutation = useMutation({ mutationFn: async (todo: any) => { const r = await apiClient.post('/todo/create', todo); return r.data.data; }, onSuccess: () => { invalidateAll(); setShowCreate(false); setNewTodo({ title: '', description: '', priority: 'medium', dueDate: '', assignedTo: '' }); } });
  const updateMutation = useMutation({ mutationFn: async ({ id, ...data }: any) => { const r = await apiClient.put(`/todo/${id}`, data); return r.data.data; }, onSuccess: invalidateAll });
  const deleteMutation = useMutation({ mutationFn: async (id: string) => { await apiClient.delete(`/todo/${id}`); }, onSuccess: invalidateAll });

  const todos = tab === 'my' ? myTodos : tab === 'assigned' ? assignedTodos : teamTodos;
  const isLoading = tab === 'my' ? loadingMy : tab === 'assigned' ? loadingAssigned : loadingTeam;
  const priorityColors: Record<string, string> = { high: 'text-red-600 dark:text-red-400 bg-red-500/15', medium: 'text-amber-700 dark:text-amber-400 bg-amber-500/15', low: 'text-green-600 dark:text-green-400 bg-green-500/15' };
  const formatDate = (d: string) => { try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return d; } };
  const formatDateTime = (d: string) => { try { const dt = new Date(d); return `${dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} at ${dt.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}`; } catch { return d; } };
  const isOverdue = (d: string) => d && new Date(d) < new Date();

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-4 animate-[slideUp_0.2s_ease-out] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium flex items-center gap-2 text-foreground">
          <CheckCircle className="h-5 w-5 text-[var(--v4-accent)]" /> Tasks
        </h2>
        <button onClick={() => setShowCreate(true)} className="px-3 py-1.5 bg-[var(--v4-accent)] text-white rounded-lg text-xs font-medium hover:bg-[var(--v4-accent-hover)] transition flex items-center gap-1">
          <Plus className="h-3.5 w-3.5" /> New Task
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-card border border-border rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-[var(--v4-accent)]">{stats.my_pending || 0}</p>
            <p className="text-[10px] text-muted-foreground">Pending</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{stats.my_in_progress || 0}</p>
            <p className="text-[10px] text-muted-foreground">In Progress</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-2.5 text-center">
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.my_completed || 0}</p>
            <p className="text-[10px] text-muted-foreground">Done</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1">
        {[
          { key: 'my' as Tab, icon: User, label: 'My Tasks' },
          { key: 'assigned' as Tab, icon: Users, label: 'Delegated' },
          { key: 'team' as Tab, icon: Users, label: 'Team' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={cn('flex-1 text-xs py-2 rounded-lg font-medium transition flex items-center justify-center gap-1', tab === t.key ? 'bg-[var(--v4-accent)] text-white' : 'text-muted-foreground')}>
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'my' && (
        <button onClick={() => setShowCompleted(!showCompleted)} className="text-xs text-muted-foreground hover:text-foreground transition">
          {showCompleted ? 'Hide completed' : 'Show completed'}
        </button>
      )}

      {/* Create Form */}
      {showCreate && (
        <div className="bg-card border border-border rounded-xl p-4 space-y-3 animate-[slideUp_0.2s_ease-out]">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">New Task</span>
            <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </div>
          <input placeholder="Task title" value={newTodo.title} onChange={e => setNewTodo({ ...newTodo, title: e.target.value })} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none" />
          <textarea placeholder="Description (optional)" value={newTodo.description} onChange={e => setNewTodo({ ...newTodo, description: e.target.value })} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none" rows={2} />
          <div className="grid grid-cols-2 gap-2">
            <select value={newTodo.priority} onChange={e => setNewTodo({ ...newTodo, priority: e.target.value })} className="bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
            </select>
            <input type="date" value={newTodo.dueDate} onChange={e => setNewTodo({ ...newTodo, dueDate: e.target.value })} className="bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground" />
          </div>
          <select value={newTodo.assignedTo} onChange={e => setNewTodo({ ...newTodo, assignedTo: e.target.value })} className="w-full bg-muted border border-border rounded-lg px-3 py-2 text-sm outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground">
            <option value="">Assign to me</option>
            {(teamMembers || []).map((m: any) => <option key={m.id} value={m.id}>{m.first_name || m.email}</option>)}
          </select>
          <button onClick={() => { if (newTodo.title) createMutation.mutate(newTodo); }} disabled={createMutation.isPending || !newTodo.title} className="w-full py-2 bg-[var(--v4-accent)] text-white rounded-lg text-sm font-medium hover:bg-[var(--v4-accent-hover)] transition disabled:opacity-40 flex items-center justify-center gap-1">
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Task
          </button>
        </div>
      )}

      {/* Todo List */}
      {isLoading ? <SkeletonPulse2 /> : (
        <div className="space-y-2">
          {(todos || []).length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">{tab === 'my' ? 'No tasks' : tab === 'assigned' ? 'No delegated tasks' : 'No team tasks'}</p>
            </div>
          ) : (todos || []).map((todo: any) => {
            const isExpanded = expandedId === todo.id;
            const source = todo.source_data ? (typeof todo.source_data === 'string' ? JSON.parse(todo.source_data) : todo.source_data) : null;

            return (
              <div key={todo.id} className={cn('rounded-xl border transition overflow-hidden', todo.status === 'completed' ? 'bg-muted/50 border-border opacity-60' : 'bg-card border-border')}>
                <div className="p-3">
                  <div className="flex items-start gap-3">
                    {/* Toggle */}
                    <button onClick={() => updateMutation.mutate({ id: todo.id, status: todo.status === 'completed' ? 'pending' : 'completed' })} className="mt-0.5 shrink-0">
                      {todo.status === 'completed' ? <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" /> : <Circle className="h-5 w-5 text-muted-foreground hover:text-[var(--v4-accent)]" />}
                    </button>

                    <div className="flex-1 min-w-0 overflow-hidden">
                      <p className={cn('text-sm font-medium break-words text-foreground', todo.status === 'completed' && 'line-through text-muted-foreground')}>{todo.title}</p>
                      {todo.description && !isExpanded && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1 break-words">{todo.description}</p>}

                      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                        <span className={cn('text-[10px] px-1.5 py-0.5 rounded capitalize', priorityColors[todo.priority] || priorityColors.medium)}>{todo.priority}</span>
                        {todo.due_date && (
                          <span className={cn('text-[10px] flex items-center gap-0.5', isOverdue(todo.due_date) && todo.status !== 'completed' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground')}>
                            <Clock className="h-2.5 w-2.5" />{formatDate(todo.due_date)}
                          </span>
                        )}
                        {todo.source_type === 'email' && <span className="text-[10px] text-[var(--v4-accent)] flex items-center gap-0.5"><Mail className="h-2.5 w-2.5" />Email</span>}
                        {tab !== 'my' && (todo.assigned_to_name || todo.assigned_to_email) && (
                          <span className="text-[10px] text-blue-600 dark:text-blue-400 flex items-center gap-0.5 truncate"><User className="h-2.5 w-2.5" />{todo.assigned_to_name || todo.assigned_to_email?.split('@')[0]}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {todo.status !== 'completed' && (
                        <select value={todo.status} onChange={e => updateMutation.mutate({ id: todo.id, status: e.target.value })} className="bg-muted border border-border rounded-lg px-1.5 py-1 text-[10px] outline-none text-foreground [&>option]:bg-card [&>option]:text-foreground max-w-[70px]">
                          <option value="pending">Pending</option>
                          <option value="in_progress">Active</option>
                          <option value="completed">Done</option>
                        </select>
                      )}
                      <button onClick={() => setExpandedId(isExpanded ? null : todo.id)} className="p-1 text-muted-foreground hover:text-foreground">
                        {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-border p-3 space-y-3 bg-muted/50 animate-[slideUp_0.15s_ease-out]">
                    {/* Description */}
                    {todo.description && (
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Description</p>
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words">{todo.description}</p>
                      </div>
                    )}

                    {/* Meta info */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div><span className="text-muted-foreground">Created:</span> <span className="text-foreground">{formatDateTime(todo.created_at)}</span></div>
                      {todo.created_by_name && <div><span className="text-muted-foreground">By:</span> <span className="text-foreground">{todo.created_by_name || todo.created_by_email}</span></div>}
                      {todo.assigned_to_name && <div><span className="text-muted-foreground">Assigned to:</span> <span className="text-foreground">{todo.assigned_to_name || todo.assigned_to_email}</span></div>}
                      {todo.completed_at && <div><span className="text-muted-foreground">Completed:</span> <span className="text-foreground">{formatDateTime(todo.completed_at)}</span></div>}
                    </div>

                    {/* Email Source - Full detail blocks matching email detail page */}
                    {todo.source_type === 'email' && source && (
                      <div className="space-y-2">
                        {/* Email Header */}
                        <div className="bg-[var(--v4-accent)]/5 border border-[var(--v4-accent)]/15 rounded-xl p-3">
                          <p className="text-[10px] text-[var(--v4-accent)] uppercase tracking-wider mb-2 flex items-center gap-1"><Mail className="h-3 w-3" /> Linked Email</p>
                          <p className="text-sm font-medium text-foreground mb-1">{source.subject}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground">
                            <span>{source.from}</span>
                            {source.date && <span>{formatDateTime(source.date)}</span>}
                          </div>
                        </div>

                        {/* Urgency + Category tags */}
                        {(source.urgency || source.category) && (
                          <div className="flex gap-2 flex-wrap">
                            {source.urgency && (
                              <span className={cn('text-[10px] px-2 py-0.5 rounded-lg border font-medium capitalize',
                                source.urgency === 'high' ? 'text-red-600 dark:text-red-400 bg-red-500/15 border-red-500/20' :
                                source.urgency === 'medium' ? 'text-amber-700 dark:text-amber-400 bg-amber-500/15 border-amber-500/20' :
                                'text-green-600 dark:text-green-400 bg-green-500/15 border-green-500/20'
                              )}>
                                {source.urgency === 'high' ? 'Urgent' : source.urgency === 'medium' ? 'Medium' : 'Low'}
                              </span>
                            )}
                            {source.category && (
                              <span className="text-[10px] px-2 py-0.5 rounded-lg bg-[var(--v4-accent)]/10 border border-[var(--v4-accent)]/20 text-[var(--v4-accent)] capitalize flex items-center gap-1">
                                <Tag className="h-2.5 w-2.5" />{source.category}
                              </span>
                            )}
                            {source.sentiment && source.sentiment !== 'neutral' && (
                              <span className={cn('text-[10px] px-2 py-0.5 rounded-lg bg-muted border border-border capitalize',
                                source.sentiment === 'positive' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              )}>{source.sentiment}</span>
                            )}
                          </div>
                        )}

                        {/* Summary block */}
                        {source.summary && (
                          <div className="bg-muted/50 border border-border rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-lg bg-[var(--v4-accent)]/15 flex items-center justify-center"><Sparkles className="h-3 w-3 text-[var(--v4-accent)]" /></div>
                              <span className="text-xs font-medium text-[var(--v4-accent)]">Summary</span>
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">{typeof source.summary === 'string' ? source.summary : JSON.stringify(source.summary)}</p>
                          </div>
                        )}

                        {/* Key Points block */}
                        {source.keyPoints && source.keyPoints.length > 0 && (
                          <div className="bg-muted/50 border border-border rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-lg bg-cyan-500/15 flex items-center justify-center"><MessageSquare className="h-3 w-3 text-cyan-600 dark:text-cyan-400" /></div>
                              <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">Key Points</span>
                            </div>
                            <ul className="space-y-1.5">
                              {source.keyPoints.map((p: string, i: number) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                                  <span className="text-cyan-600 dark:text-cyan-400 mt-0.5 shrink-0">-</span><span>{p}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Action block */}
                        {source.action && source.action.toLowerCase() !== 'no action needed' && (
                          <div className="bg-amber-500/10 border border-amber-500/15 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-lg bg-amber-500/15 flex items-center justify-center"><Bell className="h-3 w-3 text-amber-600 dark:text-amber-400" /></div>
                              <span className="text-xs font-medium text-amber-700 dark:text-amber-400">Action Required</span>
                            </div>
                            <p className="text-sm text-foreground">{source.action}</p>
                          </div>
                        )}

                        {/* Deadline block */}
                        {source.deadline && source.deadline !== 'null' && source.deadline !== null && (
                          <div className="bg-red-500/10 border border-red-500/15 rounded-xl p-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-lg bg-red-500/15 flex items-center justify-center"><Clock className="h-3 w-3 text-red-600 dark:text-red-400" /></div>
                              <span className="text-xs font-medium text-red-600 dark:text-red-400">Deadline</span>
                              <span className="text-sm text-foreground ml-1">{source.deadline}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Delete */}
                    <button onClick={() => deleteMutation.mutate(todo.id)} disabled={deleteMutation.isPending} className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition flex items-center gap-1">
                      {deleteMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />} Delete task
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
